import React, { useEffect, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { useWindowSize } from "go-core/components/useWindowSize";
import { apiPublic } from "../../../../services/Api/Public/apiPublic";
import { NumberOrderApi, NumberOrderOrganizationInfoApi } from "../../../../services/Api/Public/types";
import NumberSystemMobileContainer from "./components/Mobile/NumberSystemMobileContainer";
import NumberSystemContainer from "./components/NumberSystemContainer";
import { NumberSystemSynchronizer } from "./services/numberSystemSynchronizer";

interface MatchParams {
	organizationId: string;
}

const NumberSystemPage = () => {
	const { organizationId } = useParams<MatchParams>();
	const timeoutRef = useRef<any>(null);
	const numberSystemSynchronizer = useRef(new NumberSystemSynchronizer());
	const interval = useRef<number>(5000);
	const [items, setItems] = useState<NumberOrderApi[]>([]);
	const [itemsLoading, setItemsLoading] = useState(true);
	const itemsRef = useRef<NumberOrderApi[]>([]);
	const screenWidth = useWindowSize().width;
	const { addFlash } = useFlash();
	const [organizationInfo, setOrganizationInfo] = useState<NumberOrderOrganizationInfoApi | undefined>();
	const [kdsNumberEnabled, setKdsNumberEnabled] = useState(true);
	const { t } = useTranslation();
	const syncItemsInProgress = useRef(false);

	useEffect(() => {
		return () => {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		};
	}, []);

	useEffect(() => {
		handleKdsNumberEnabledChange();
	}, [kdsNumberEnabled]);

	const handleKdsNumberEnabledChange = async () => {
		if (kdsNumberEnabled) {
			if (!organizationInfo && kdsNumberEnabled) await fetchOrganizationInfo();

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = 1;
			synchronize(true);
		}
	};

	const findKdsNotEnabledError = (errors: ApiError[]) => {
		return errors.find((error) => error?.code === "kds_number_not_enabled");
	};

	const fetchOrganizationInfo = async () => {
		try {
			const res = await apiPublic.getOrderNumbersOrganizationInfo(Number(organizationId));
			setOrganizationInfo(res);
		} catch (err) {
			const errors = handleException(err);
			if (!findKdsNotEnabledError(errors)) {
				handleError.alert(err, addFlash);
			}
		}
	};

	const synchronize = async (firstSync: boolean) => {
		if (syncItemsInProgress.current) return;

		if (firstSync) {
			setItemsLoading(true);
		}

		try {
			syncItemsInProgress.current = true;
			if (!timeoutRef.current) return;

			const newItems = await numberSystemSynchronizer.current.sync(Number(organizationId));
			setKdsNumberEnabled(true);
			if (JSON.stringify(newItems) !== JSON.stringify(itemsRef.current)) {
				setItems([...newItems]);
				itemsRef.current = [...newItems];
			}

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			startTimeout();
		} catch (err) {
			const errors = handleException(err);
			const kdsNumberEnabledError = errors.find((error) => error?.code === "kds_number_not_enabled");
			if (kdsNumberEnabled && kdsNumberEnabledError) {
				setKdsNumberEnabled(false);
			}
		} finally {
			syncItemsInProgress.current = false;
		}

		if (firstSync) {
			startTimeout();
			setItemsLoading(false);
		}
	};

	const startTimeout = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = window.setTimeout(() => {
			if (timeoutRef.current) {
				synchronize(false);
				startTimeout();
			}
		}, interval.current);
	};

	return (
		<div className="page">
			<div className="page-container">
				<div className="content p-0 overflow-y-hidden">
					{!kdsNumberEnabled && (
						<Alert key={`${kdsNumberEnabled}`} className="rounded-0" variant="danger">
							{t("modules.number_system.field.kds_not_enabled.title")}
						</Alert>
					)}
					{screenWidth > 599 ? (
						<NumberSystemContainer showItems={kdsNumberEnabled} items={items} itemsLoading={itemsLoading} />
					) : (
						<NumberSystemMobileContainer
							items={items}
							itemsLoading={itemsLoading}
							organizationInfo={organizationInfo}
							showItems={kdsNumberEnabled}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default NumberSystemPage;
